<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useQuasar } from 'quasar'
import { storeToRefs } from 'pinia'
import type {
  IncidentReport,
  UserIncidentFootage,
  UserIncidentImage
} from '@roadfootage/api-client'
import type { MediaKind } from '../../stores/incident-media'
import { useIncidentMediaStore } from '../../stores/incident-media'
import MediaFileCard from './MediaFileCard.vue'
import MediaDownloadDialog from './MediaDownloadDialog.vue'

// The "Media" tab of the Incident Reports list modal (RF-128). `report` is the clone the modal's JSON Patch
// observer watches, so it is read-only here: removals are tracked in the store, never by editing it.
const props = defineProps<{ report: IncidentReport }>()
const emit = defineEmits<{ changed: [] }>()

const $q = useQuasar()
const store = useIncidentMediaStore()
const { urlsByFileId, processingByFileId, removedIds, loadingUrls } = storeToRefs(store)

const PROCESSING_POLL_MS = 5000
let pollTimer: ReturnType<typeof setInterval> | null = null

const allFootage = computed(
  () => (props.report.userIncidentFootageCollection ?? []) as UserIncidentFootage[]
)
const allImages = computed(
  () => (props.report.userIncidentImageCollection ?? []) as UserIncidentImage[]
)
const footage = computed(() => allFootage.value.filter((f) => !removedIds.value.includes(f.id!)))
const images = computed(() => allImages.value.filter((f) => !removedIds.value.includes(f.id!)))

// The live pipeline answer wins over the status the list loaded with, so a reprocess shows up without reloading.
const statusOf = (file: UserIncidentFootage | UserIncidentImage) =>
  processingByFileId.value[file.id!]?.status ?? file.mediaProcessingStatus

const downloadTarget = ref<{ id: string; name: string } | null>(null)
const downloadOpen = computed({
  get: () => downloadTarget.value !== null,
  set: (open) => {
    if (!open) downloadTarget.value = null
  }
})

function stopPolling() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
}

function start() {
  stopPolling()
  const id = props.report.id
  if (!id) return
  void store.open(id, allFootage.value, allImages.value)
  pollTimer = setInterval(() => void store.refreshProcessing(), PROCESSING_POLL_MS)
}

onMounted(start)
watch(() => props.report.id, start)
onUnmounted(() => {
  stopPolling()
  store.reset()
})

function confirmRemove(file: UserIncidentFootage | UserIncidentImage, kind: MediaKind) {
  const label = kind === 'video' ? 'footage' : 'image'
  const isPrimary = props.report.primaryUserIncidentFileId === file.id
  const primaryNote = isPrimary
    ? ' This is the primary media; pick another in the review screen.'
    : ''
  $q.dialog({
    title: `Remove ${label}`,
    message: `Remove "${file.uploadFileName ?? 'this file'}" from this report? It is hidden, not erased.${primaryNote}`,
    cancel: true,
    ok: { label: 'Remove', color: 'negative', flat: true }
  }).onOk(async () => {
    try {
      await store.remove(file.id!, kind)
    } catch (e) {
      console.error(`Error removing file ${file.id}:`, e)
      $q.notify({ type: 'negative', message: `Failed to remove ${label}.` })
      return
    }
    $q.notify({ type: 'positive', message: `${label[0].toUpperCase()}${label.slice(1)} removed.` })
    emit('changed')
  })
}

async function reprocess(file: UserIncidentFootage | UserIncidentImage) {
  try {
    await store.reprocess(file.id!)
  } catch (e) {
    console.error(`Error reprocessing file ${file.id}:`, e)
    $q.notify({ type: 'negative', message: 'Failed to trigger reprocessing.' })
    return
  }
  $q.notify({ type: 'positive', message: 'Reprocessing triggered.' })
  void store.refreshProcessing()
}
</script>

<template>
  <div data-testid="incident-media">
    <template v-if="footage.length">
      <div class="text-caption text-grey-6 text-uppercase q-mb-xs row items-center q-gutter-x-xs">
        <q-icon name="mdi-video" size="xs" />
        <span>Footage ({{ footage.length }})</span>
      </div>
      <div class="row wrap q-gutter-sm">
        <MediaFileCard
          v-for="file in footage"
          :key="file.id"
          :file="file"
          :url-data="urlsByFileId[file.id!]"
          :media-status="statusOf(file)"
          :processing="processingByFileId[file.id!]"
          :is-selected="false"
          :is-viewed="false"
          :is-primary="report.primaryUserIncidentFileId === file.id"
          :is-loading="loadingUrls"
          :show-primary-toggle="false"
          media-type="video"
          @delete="confirmRemove(file, 'video')"
          @download="downloadTarget = { id: file.id!, name: file.uploadFileName ?? 'file' }"
          @reprocess="reprocess(file)"
        />
      </div>
    </template>

    <template v-if="images.length">
      <div class="text-caption text-grey-6 text-uppercase q-my-xs row items-center q-gutter-x-xs">
        <q-icon name="mdi-image" size="xs" />
        <span>Images ({{ images.length }})</span>
      </div>
      <div class="row wrap q-gutter-sm">
        <MediaFileCard
          v-for="file in images"
          :key="file.id"
          :file="file"
          :url-data="urlsByFileId[file.id!]"
          :media-status="statusOf(file)"
          :processing="processingByFileId[file.id!]"
          :is-selected="false"
          :is-viewed="false"
          :is-primary="report.primaryUserIncidentFileId === file.id"
          :is-loading="loadingUrls"
          :show-primary-toggle="false"
          media-type="image"
          @delete="confirmRemove(file, 'image')"
          @download="downloadTarget = { id: file.id!, name: file.uploadFileName ?? 'file' }"
          @reprocess="reprocess(file)"
        />
      </div>
    </template>

    <div v-if="!footage.length && !images.length" class="text-grey-6 text-center q-pa-md">
      No media on this report.
    </div>

    <MediaDownloadDialog
      v-model="downloadOpen"
      :user-file-id="downloadTarget?.id ?? null"
      :file-name="downloadTarget?.name ?? ''"
    />
  </div>
</template>
